// Integrations panel — Connect / Sync / Disconnect YouTube + Instagram.
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Youtube, Instagram, Plug, RotateCw, Trash2, Loader2, CheckCircle2, Clock } from "lucide-react";
import { client } from "@/lib/api";

const INK_55 = "var(--ink-55)";
const SEAL = "var(--seal)";

const META = {
  youtube: {
    icon: Youtube,
    name: "YouTube",
    accent: "#FF0033",
    statLabels: { subscribers: "Subscribers", views: "Total views", videos: "Videos" },
    coming: "Live as soon as your Google OAuth client is added.",
  },
  instagram: {
    icon: Instagram,
    name: "Instagram",
    accent: "#E1306C",
    statLabels: { followers: "Followers", media: "Posts" },
    coming: "Awaiting Meta App Review — usually 5-14 days.",
  },
};

export default function IntegrationsCard() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null); // provider currently working
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await client.get("/integrations/status");
      setStatus(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Surface OAuth-callback results coming back via ?integration=...&status=...
  useEffect(() => {
    const provider = params.get("integration");
    const result = params.get("status");
    if (!provider || !result) return;
    if (result === "connected") {
      toast.success(`${META[provider]?.name || provider} connected`);
      load();
    } else if (result === "denied") {
      toast.error("Connection cancelled");
    } else if (result === "expired") {
      toast.error("Auth window expired — try again");
    } else if (result === "token_error") {
      toast.error("Couldn't exchange the auth code — try again");
    }
    // Clean the URL so we don't re-fire.
    params.delete("integration");
    params.delete("status");
    setParams(params, { replace: true });
  }, [params, setParams]);

  const connect = async (provider) => {
    setBusy(provider);
    try {
      const r = await client.post(`/integrations/${provider}/connect`);
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start connection");
      setBusy(null);
    }
  };

  const sync = async (provider) => {
    setBusy(provider);
    try {
      const r = await client.post(`/integrations/${provider}/sync`);
      const parts = Object.entries(r.data.stats || {}).map(
        ([k, v]) => `${META[provider].statLabels[k] || k}: ${v.toLocaleString()}`
      );
      if (typeof r.data.videos_synced === "number") {
        parts.push(`${r.data.videos_synced} videos imported into Analytics`);
      }
      toast.success(`${META[provider].name} stats refreshed`, { description: parts.join(" · ") });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async (provider) => {
    if (!window.confirm(`Disconnect ${META[provider].name}? You can reconnect anytime.`)) return;
    setBusy(provider);
    try {
      await client.delete(`/integrations/${provider}`);
      toast.success(`${META[provider].name} disconnected`);
      load();
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="card flex items-center gap-2 text-sm" style={{ color: INK_55 }}>
        <Loader2 size={14} className="animate-spin" /> Loading integrations…
      </div>
    );
  }

  return (
    <div className="card">
      <div className="eyebrow mb-4">Connected accounts</div>
      <div className="text-xs mb-5" style={{ color: INK_55 }}>
        Pull real numbers — subscribers, views, posts — instead of typing them in. Auto-syncs every 12 hours.
      </div>

      <div className="space-y-3">
        {["youtube", "instagram"].map((provider) => {
          const m = META[provider];
          const Icon = m.icon;
          const data = status?.[provider];
          const isBusy = busy === provider;
          return (
            <div
              key={provider}
              className="rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-4"
              style={{ background: "var(--bg)", border: "1px solid var(--ink-22)" }}
              data-testid={`integration-${provider}`}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: `${m.accent}14`, border: `1px solid ${m.accent}44` }}
                >
                  <Icon size={18} style={{ color: m.accent }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-heading text-base flex items-center gap-2" style={{ fontVariationSettings: "'opsz' 32, 'wght' 500" }}>
                    {m.name}
                    {data?.connected && <CheckCircle2 size={13} style={{ color: SEAL }} />}
                  </div>
                  {data?.connected ? (
                    <div className="text-xs mt-0.5 truncate" style={{ color: INK_55 }}>
                      {data.account?.account_label}
                      {data.account?.stats && (
                        <span>
                          {" · "}
                          {Object.entries(data.account.stats)
                            .map(([k, v]) => `${(v || 0).toLocaleString()} ${m.statLabels[k] || k}`)
                            .join(" · ")}
                        </span>
                      )}
                    </div>
                  ) : !data?.configured ? (
                    <div className="text-xs mt-0.5 flex items-center gap-1.5" style={{ color: INK_55 }}>
                      <Clock size={10} /> {m.coming}
                    </div>
                  ) : (
                    <div className="text-xs mt-0.5" style={{ color: INK_55 }}>Not connected</div>
                  )}
                </div>
              </div>

              <div className="flex gap-2 flex-shrink-0">
                {data?.connected ? (
                  <>
                    <button
                      onClick={() => sync(provider)}
                      disabled={isBusy}
                      className="btn-secondary text-xs"
                      data-testid={`integration-${provider}-sync`}
                    >
                      {isBusy ? <Loader2 size={12} className="animate-spin" /> : <RotateCw size={12} />}
                      Sync
                    </button>
                    <button
                      onClick={() => disconnect(provider)}
                      disabled={isBusy}
                      className="btn-ghost text-xs"
                      style={{ color: "#c97a6a" }}
                      data-testid={`integration-${provider}-disconnect`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => connect(provider)}
                    disabled={isBusy || !data?.configured}
                    className={data?.configured ? "btn-primary text-xs" : "btn-secondary text-xs"}
                    data-testid={`integration-${provider}-connect`}
                    style={{ touchAction: "manipulation" }}
                    title={!data?.configured ? "Setup pending" : "Connect"}
                  >
                    {isBusy ? <Loader2 size={12} className="animate-spin" /> : <Plug size={12} />}
                    {data?.configured ? "Connect" : "Coming soon"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
